import {
  CalendarRange,
  Activity,
  Building2,
  BookOpen,
  DatabaseZap,
  Flag,
  History,
  Gavel,
  GitFork,
  LayoutDashboard,
  Link2,
  Scale,
  Settings2,
  Radio,
  ShieldEllipsis,
  ShieldCheck,
  ScrollText,
  Store,
  Timer,
  Trophy,
  UserCog,
  Users,
  Workflow,
  Wrench,
} from "lucide-react";
import type { ComponentType } from "react";

export interface NavigationItem {
  label: string;
  href: string;
  icon: ComponentType<{ size?: number; strokeWidth?: number; "aria-hidden"?: boolean }>;
  commissioner?: boolean;
}

export const primaryNavigation: readonly NavigationItem[] = [
  { label: "League", href: "/league", icon: Building2 },
  { label: "League Office", href: "/league/office", icon: BookOpen },
  { label: "My Team", href: "/team/fixture-team", icon: ShieldCheck },
  { label: "Contracts", href: "/contracts", icon: ScrollText },
  { label: "Season", href: "/season", icon: CalendarRange },
  { label: "Auction", href: "/auction", icon: Timer },
  { label: "Market", href: "/market", icon: Store },
  { label: "Trades", href: "/trades", icon: Scale },
  { label: "Rehearsal", href: "/rehearsal", icon: GitFork },
  { label: "Draft", href: "/draft", icon: Users },
  { label: "Game Day", href: "/gameday", icon: Trophy },
  { label: "Playoffs", href: "/playoffs", icon: Flag },
  { label: "History", href: "/history", icon: History },
  { label: "Players", href: "/players", icon: Activity },
] as const;

export const commissionerNavigation: readonly NavigationItem[] = [
  { label: "Commissioner", href: "/commissioner", icon: Gavel, commissioner: true },
  {
    label: "Season Rehearsal",
    href: "/commissioner/simulation",
    icon: Workflow,
    commissioner: true,
  },
  {
    label: "League Settings",
    href: "/commissioner/settings",
    icon: Settings2,
    commissioner: true,
  },
  {
    label: "MFL Operations",
    href: "/commissioner/mfl",
    icon: Radio,
    commissioner: true,
  },
  {
    label: "Membership",
    href: "/commissioner/membership",
    icon: UserCog,
    commissioner: true,
  },
  {
    label: "MFL Mappings",
    href: "/commissioner/mfl/mappings",
    icon: Link2,
    commissioner: true,
  },
  {
    label: "Configure Mappings",
    href: "/commissioner/mfl/mappings/configure",
    icon: Wrench,
    commissioner: true,
  },
  {
    label: "Integrity Center",
    href: "/commissioner/health",
    icon: ShieldEllipsis,
    commissioner: true,
  },
  {
    label: "Data Health",
    href: "/commissioner/data-health",
    icon: DatabaseZap,
    commissioner: true,
  },
] as const;

export const mobileNavigation: readonly NavigationItem[] = [
  { label: "Home", href: "/league", icon: LayoutDashboard },
  { label: "Team", href: "/team/fixture-team", icon: ShieldCheck },
  { label: "Contracts", href: "/contracts", icon: ScrollText },
  { label: "Game Day", href: "/gameday", icon: Trophy },
  { label: "Office", href: "/league/office", icon: BookOpen },
] as const;

export const LAB_STAGE_IDS = [
  "startup_auction",
  "season_open",
  "season_closed",
  "expiration",
  "extension",
  "rookie_draft",
  "cuts",
  "compliance",
] as const;

export type LabStageId = (typeof LAB_STAGE_IDS)[number];

export const isLabStageId = (value: string | null | undefined): value is LabStageId =>
  value === "startup_auction" ||
  value === "season_open" ||
  value === "season_closed" ||
  value === "expiration" ||
  value === "extension" ||
  value === "rookie_draft" ||
  value === "cuts" ||
  value === "compliance";

const dominantHrefForStage = (stage: LabStageId): string => {
  switch (stage) {
    case "startup_auction":
      return "/auction";
    case "season_open":
      return "/gameday";
    case "season_closed":
      return "/season";
    case "expiration":
      return "/contracts";
    case "extension":
      return "/season";
    case "rookie_draft":
      return "/draft";
    case "cuts":
      return "/contracts";
    case "compliance":
      return "/season";
    default: {
      const exhaustive: never = stage;
      return exhaustive;
    }
  }
};

const itemByHref = (
  href: string,
  catalog: readonly NavigationItem[],
): NavigationItem | undefined => catalog.find((item) => item.href === href);

export const stageDrivenPrimaryNavigation = (
  stage: LabStageId | null,
): readonly NavigationItem[] => {
  if (stage === null) {
    return primaryNavigation;
  }
  const dominant = itemByHref(dominantHrefForStage(stage), primaryNavigation);
  if (dominant === undefined) {
    return primaryNavigation;
  }
  return [dominant, ...primaryNavigation.filter((item) => item.href !== dominant.href)];
};

export const stageDrivenMobileNavigation = (
  stage: LabStageId | null,
): readonly NavigationItem[] => {
  if (stage === null) {
    return mobileNavigation;
  }
  const dominantHref = dominantHrefForStage(stage);
  const dominant =
    itemByHref(dominantHref, primaryNavigation) ??
    itemByHref(dominantHref, mobileNavigation);
  const home = mobileNavigation[0];
  const office = itemByHref("/league/office", mobileNavigation);
  if (dominant === undefined || home === undefined || office === undefined) {
    return mobileNavigation;
  }

  const remaining = mobileNavigation.filter(
    (item) =>
      item.href !== home.href &&
      item.href !== office.href &&
      item.href !== dominant.href,
  );
  const ordered: NavigationItem[] = [home];
  if (dominant.href !== home.href && dominant.href !== office.href) {
    ordered.push(dominant);
  }
  for (const item of remaining) {
    if (ordered.length >= 4) {
      break;
    }
    ordered.push(item);
  }
  ordered.push(office);
  return ordered.slice(0, 5);
};

export const dominantHrefForLabStage = dominantHrefForStage;
